import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { UserButton, useClerkAppearance } from '../../auth/clerk';
import { Menu, Settings } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import JourneyHereChip from './JourneyHereChip';
import { isSeriesAltitude } from './sidebarMap';
import './MobileHeader.css';

export default function MobileHeader({
  onMenuOpen,
  onOpenCaptures,
  onOpenBookmarks,
  onOpenLearn,
  onOpenSettings,
}) {
  const { currentProject, currentStory, currentSession } = useApp();
  const clerkAppearance = useClerkAppearance({});
  const location = useLocation();
  const [showOverflow, setShowOverflow] = useState(false);
  const atSeriesAltitude = isSeriesAltitude({
    isSeries: Boolean(currentProject?.is_series),
    pathname: location.pathname,
    currentStory,
    currentSession,
  });
  const showJourneyChip = Boolean(currentProject) && !atSeriesAltitude;

  const breadcrumbParts = [];
  if (currentProject) {
    breadcrumbParts.push(currentProject.name || 'Project');
  }
  if (currentStory) {
    breadcrumbParts.push(currentStory.name || 'Story');
  }
  if (currentSession) {
    breadcrumbParts.push(currentSession.name || 'Session');
  }

  // Close overflow menu on Escape
  useEffect(() => {
    if (!showOverflow) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setShowOverflow(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showOverflow]);

  const handleOverflowItem = (action) => {
    setShowOverflow(false);
    action();
  };

  return (
    <header className="mobile-header">
      <button
        className="mobile-header__hamburger"
        onClick={onMenuOpen}
        aria-label="Open navigation"
      >
        <Menu size={22} />
      </button>

      {showJourneyChip ? (
        <JourneyHereChip />
      ) : (
        <div className="mobile-header__breadcrumb">
          {breadcrumbParts.length === 0 ? (
            'Brainstormy'
          ) : (
            breadcrumbParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="mobile-header__breadcrumb-separator">›</span>}
                {part}
              </React.Fragment>
            ))
          )}
        </div>
      )}

      <div className="mobile-header__right">
        <UserButton afterSignOutUrl="/sign-in" appearance={clerkAppearance} />
        <div className="mobile-header__overflow-anchor">
          <button
            className="mobile-header__overflow"
            onClick={() => setShowOverflow(!showOverflow)}
            aria-label="More options"
            aria-expanded={showOverflow}
            aria-haspopup="menu"
          >
            <Settings size={20} />
          </button>

          {showOverflow && (
            <>
              <div
                className="mobile-header__overflow-backdrop"
                onClick={() => setShowOverflow(false)}
              />
              <div
                className="mobile-header__overflow-menu mobile-header__overflow-menu--anchored"
                role="menu"
              >
                {onOpenCaptures && (
                  <button className="mobile-header__overflow-item" role="menuitem" onClick={() => handleOverflowItem(onOpenCaptures)}>
                    Captures
                  </button>
                )}
                {onOpenBookmarks && (
                  <button className="mobile-header__overflow-item" role="menuitem" onClick={() => handleOverflowItem(onOpenBookmarks)}>
                    Bookmarks
                  </button>
                )}
                {onOpenLearn && (
                  <button className="mobile-header__overflow-item" role="menuitem" onClick={() => handleOverflowItem(onOpenLearn)}>
                    Learn
                  </button>
                )}
                {onOpenSettings && (
                  <button className="mobile-header__overflow-item" role="menuitem" onClick={() => handleOverflowItem(onOpenSettings)}>
                    Settings
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
